/**
 * Raw Data Integrity Checker
 * 
 * Provides utilities to validate and ensure raw booking data is complete and accurate
 */

/**
 * Validates that rawForm1Data contains all required fields
 * @param {Object} rawForm1Data - The raw data from Form 1
 * @returns {Object} { isValid: boolean, missingFields: string[], errors: string[] }
 */
export const validateRawForm1Data = (rawForm1Data) => {
    const result = {
        isValid: true,
        missingFields: [],
        errors: [],
    };

    // Check if rawForm1Data exists
    if (!rawForm1Data || typeof rawForm1Data !== 'object') {
        result.isValid = false;
        result.errors.push('rawForm1Data is null, undefined, or not an object');
        return result;
    }

    // Check if it's an empty object
    if (Object.keys(rawForm1Data).length === 0) {
        result.isValid = false;
        result.errors.push('rawForm1Data is an empty object');
        return result;
    }

    // Define required fields for Form 1
    const requiredFields = [
        'pickUpAddress',
        'dropOffAddress',
        'departureDate',
        'departureTime',
        'noOfPassengers',
    ];

    // Check for missing required fields
    requiredFields.forEach(field => {
        if (!rawForm1Data[field]) {
            result.isValid = false;
            result.missingFields.push(field);
        }
    });

    // Check for coordinates (not strictly required but should warn)
    if (!rawForm1Data.pickupLat || !rawForm1Data.pickupLong) {
        result.errors.push('Warning: Missing pickup coordinates');
    }
    if (!rawForm1Data.dropOffLat || !rawForm1Data.dropOffLong) {
        result.errors.push('Warning: Missing dropoff coordinates');
    }

    // Check if capturedAt timestamp exists
    if (!rawForm1Data.capturedAt) {
        result.errors.push('Warning: Missing capturedAt timestamp');
    }

    return result;
};

/**
 * Validates that rawForm2Data contains all required fields
 * @param {Object} rawForm2Data - The raw data from Form 2
 * @returns {Object} { isValid: boolean, missingFields: string[], errors: string[] }
 */
export const validateRawForm2Data = (rawForm2Data) => {
    const result = {
        isValid: true,
        missingFields: [],
        errors: [],
    };

    if (!rawForm2Data || typeof rawForm2Data !== 'object') {
        result.isValid = false;
        result.errors.push('rawForm2Data is null, undefined, or not an object');
        return result;
    }

    // Define required fields for Form 2
    const requiredFields = [
        'firstName',
        'lastName',
        'email',
        'phone',
    ];

    // Check for missing required fields
    requiredFields.forEach(field => {
        if (!rawForm2Data[field]) {
            result.isValid = false;
            result.missingFields.push(field);
        }
    });

    // Check if capturedAt timestamp exists
    if (!rawForm2Data.capturedAt) {
        result.errors.push('Warning: Missing capturedAt timestamp');
    }

    return result;
};

/**
 * Validates complete raw data object before submission
 * @param {Object} completeRawData - Combined raw data from both forms
 * @returns {Object} { isValid: boolean, errors: string[], fieldCounts: Object }
 */
export const validateCompleteRawData = (completeRawData) => {
    const result = {
        isValid: true,
        errors: [],
        fieldCounts: {
            total: 0,
            form1: 0,
            form2: 0,
        },
    };

    if (!completeRawData || typeof completeRawData !== 'object') {
        result.isValid = false;
        result.errors.push('Complete raw data is null, undefined, or not an object');
        return result;
    }

    // Count fields
    result.fieldCounts.total = Object.keys(completeRawData).length;

    // Check for essential fields from Form 1
    const form1Fields = ['pickUpAddress', 'dropOffAddress', 'departureDate', 'departureTime'];
    form1Fields.forEach(field => {
        if (completeRawData[field]) {
            result.fieldCounts.form1++;
        } else {
            result.isValid = false;
            result.errors.push(`Missing Form 1 field: ${field}`);
        }
    });

    // Check for essential fields from Form 2
    const form2Fields = ['firstName', 'lastName', 'email', 'phone'];
    form2Fields.forEach(field => {
        if (completeRawData[field]) {
            result.fieldCounts.form2++;
        } else {
            result.isValid = false;
            result.errors.push(`Missing Form 2 field: ${field}`);
        }
    });

    // Check for submission metadata
    if (!completeRawData.submittedAt) {
        result.errors.push('Warning: Missing submittedAt timestamp');
    }
    if (!completeRawData.browserType) {
        result.errors.push('Warning: Missing browser type');
    }

    return result;
};

/**
 * Attempts to recover rawForm1Data from storage
 * @returns {Object|null} Recovered rawForm1Data or null if not found
 */
export const attemptRawDataRecovery = () => {

    try {
        // Try primary storage
        const storedBookingData = sessionStorage.getItem('booking_form1');
        if (storedBookingData) {
            const parsedData = JSON.parse(storedBookingData);
            if (parsedData.rawForm1Data) {
                return parsedData.rawForm1Data;
            }
        }

        // Try backup storage
        const backupRawData = sessionStorage.getItem('booking_raw_form1_backup');
        if (backupRawData) {
            const parsedBackup = JSON.parse(backupRawData);
            return parsedBackup;
        }

        // Try emergency storage
        const emergencyData = localStorage.getItem('booking_raw_form1_emergency');
        if (emergencyData) {
            const parsedEmergency = JSON.parse(emergencyData);
            return parsedEmergency;
        }

        console.error('[RAW_DATA/INTEGRITY] ❌ No recovery data found in any storage');
        return null;
    } catch (error) {
        console.error('[RAW_DATA/INTEGRITY] ❌ Recovery failed:', error);
        return null;
    }
};

/**
 * Clears all raw data backups from storage (call after successful submission)
 */
export const clearRawDataBackups = () => {
    try {
        sessionStorage.removeItem('booking_form1');
        sessionStorage.removeItem('booking_raw_form1_backup');
        sessionStorage.removeItem('booking_complete_raw_data');
        localStorage.removeItem('booking_raw_form1_emergency');
    } catch (error) {
        console.error('[RAW_DATA/INTEGRITY] ⚠️ Error clearing backups:', error);
    }
};

/**
 * Logs detailed information about current raw data state
 * @param {Object} bookingData - Current booking data from Redux
 */
export const logRawDataState = (bookingData) => {
    console.group('[RAW_DATA/INTEGRITY] Current State');
    
    console.log('Has rawForm1Data:', !!bookingData.rawForm1Data);
    
    if (bookingData.rawForm1Data) {
        console.log('rawForm1Data fields:', Object.keys(bookingData.rawForm1Data));
        console.log('rawForm1Data validation:', validateRawForm1Data(bookingData.rawForm1Data));
    } else {
        console.warn('rawForm1Data is missing!');
    }
    
    // Check storage
    const hasSessionStorage = !!sessionStorage.getItem('booking_form1');
    const hasBackup = !!sessionStorage.getItem('booking_raw_form1_backup');
    const hasEmergency = !!localStorage.getItem('booking_raw_form1_emergency');
    
    console.log('Storage status:', {
        sessionStorage: hasSessionStorage,
        backup: hasBackup,
        emergency: hasEmergency,
    });
    
    console.groupEnd();
};

/**
 * Validates processed booking form data from Redux (Form 1 / trip details)
 * This runs on the second form submit to make sure the trip info is still complete.
 *
 * @param {Object} bookingData - Redux booking slice state
 * @returns {Object} { isValid: boolean, missingFields: string[], errors: string[] }
 */
export const validateBookingFormData = (bookingData) => {
    const result = {
        isValid: true,
        missingFields: [],
        errors: [],
    };

    if (!bookingData || typeof bookingData !== 'object') {
        result.isValid = false;
        result.errors.push('bookingData is null, undefined, or not an object');
        return result;
    }

    // Core required fields from Form 1 (trip details)
    const requiredFields = [
        'pickUpAddress',
        'dropOffAddress',
        'departureDate',
        'departureTime',
        'tripType',
        'noOfPassengers',
    ];

    requiredFields.forEach((field) => {
        const value = bookingData[field];
        const isMissing = value === null || value === undefined || value === '';

        if (isMissing) {
            result.isValid = false;
            result.missingFields.push(field);
        }
    });

    // If trip type is "2" (return trip in current implementation), require return date/time
    if (bookingData.tripType === '2') {
        if (!bookingData.returnDate) {
            result.isValid = false;
            result.missingFields.push('returnDate');
        }
        if (!bookingData.returnTime) {
            result.isValid = false;
            result.missingFields.push('returnTime');
        }
    }

    // Validate passengers (must be a positive number)
    const passengers = parseInt(bookingData.noOfPassengers, 10);
    if (Number.isNaN(passengers) || passengers <= 0) {
        result.isValid = false;
        result.errors.push('noOfPassengers must be a positive number');
        if (!result.missingFields.includes('noOfPassengers')) {
            result.missingFields.push('noOfPassengers');
        }
    }

    return result;
};


